'use client'

import {
   Badge,
   Box,
   Card,
   Container,
   Group,
   SimpleGrid,
   Stack,
   Text,
   ThemeIcon
} from '@blocks'
import {
   siteFeatureCardBackgroundHex,
   siteFeatureCardShadow,
   siteFeatureCardMutedLabelHex,
   siteSectionBodyHex,
   siteSectionCardRadiusPx,
   siteSectionHeadingHex,
   siteTextOnDarkBandHex,
   siteTextOnDarkBandMutedHex,
   siteTypographyEyebrowFontSizeDesktop,
   siteTypographyEyebrowFontSizeMobile,
   siteTypographySectionLeadFontSizeDesktop,
   siteTypographySectionLeadFontSizeMobile,
   siteTypographySectionTitleDisplayFontSizeDesktop,
   siteTypographySectionTitleDisplayFontSizeMobile,
   siteTypographySectionTitlePrimaryFontSizeDesktop,
   siteTypographySectionTitlePrimaryFontSizeMobile,
   siteTypographySectionTitleSecondaryFontSizeDesktop,
   siteTypographySectionTitleSecondaryFontSizeMobile
} from '@constants'
import {useMediaQuery} from '@hooks'
import {IconBrush, IconCheck, IconSparkles} from '@icons'

const whoItIsFor = [
   'Everyday vehicles needing a full refresh',
   'Work vehicles that require extra care',
   'Family cars with built-up interior mess',
   'Vehicles being prepared for sale',
   'Customers wanting a cleaner, more polished finish'
]

const interiorItems = [
   'Full interior vacuum',
   'Wipe-down of surfaces',
   'Deep cleaning of high-touch areas',
   'Seat and carpet cleaning',
   'Removal of dust, dirt, and everyday build-up',
   'Interior freshening for a cleaner overall feel'
]

const exteriorItems = [
   'Hand wash and detailed exterior clean',
   'Wheels, tyres, and trims cleaned',
   'Removal of road grime and built-up dirt',
   'Cut & polish and paint correction',
   'Protective products to help maintain the result'
]

export const Detailing = () => {
   const isMobile = useMediaQuery('(max-width: 768px)')

   return (
      <Stack gap={0}>
         <Box bg={'primary.9'}>
            <Container
               py={isMobile ? 80 : 120}
               size={'xl'}
            >
               <Stack gap={60}>
                  <Stack
                     align={'center'}
                     gap={12}
                  >
                     <Badge
                        size={'lg'}
                        fw={700}
                        tt={'uppercase'}
                        styles={{
                           root: {
                              backgroundColor:
                                 'rgba(255,255,255,0.12)',
                              color: 'white',
                              border:
                                 '1px solid rgba(255,255,255,0.22)'
                           }
                        }}
                     >
                        Clean Park Detailing & Grooming
                     </Badge>
                     <Text
                        c={siteTextOnDarkBandHex}
                        fz={
                           isMobile
                              ? siteTypographySectionTitleDisplayFontSizeMobile
                              : siteTypographySectionTitleDisplayFontSizeDesktop
                        }
                        fw={800}
                        lh={1.05}
                        ta={'center'}
                     >
                        A More Complete Clean.
                     </Text>
                     <Stack
                        gap={14}
                        maw={640}
                        align={'center'}
                     >
                        <Text
                           c={siteTextOnDarkBandMutedHex}
                           fz={
                              isMobile
                                 ? siteTypographySectionLeadFontSizeMobile
                                 : siteTypographySectionLeadFontSizeDesktop
                           }
                           fw={500}
                           lh={1.7}
                           ta={'center'}
                        >
                           For vehicles that need more than a standard
                           wash, Clean Park Detailing offers a higher
                           level of care and attention. Based in
                           Morrinsville, we provide professional
                           vehicle detailing services designed to
                           restore, refresh, and protect your car
                           inside and out.
                        </Text>
                        <Text
                           c={siteTextOnDarkBandMutedHex}
                           fz={
                              isMobile
                                 ? siteTypographySectionLeadFontSizeMobile
                                 : siteTypographySectionLeadFontSizeDesktop
                           }
                           fw={500}
                           lh={1.7}
                           ta={'center'}
                        >
                           Whether you want to maintain a well-kept
                           vehicle, prepare it for sale, or bring back
                           a cleaner, sharper finish, our detailing
                           service is focused on quality results and
                           careful workmanship.
                        </Text>
                     </Stack>
                  </Stack>
               </Stack>
            </Container>
         </Box>

         <Box bg={'#f4f7ff'}>
            <Container
               py={isMobile ? 70 : 100}
               size={'xl'}
            >
               <Group
                  align={'stretch'}
                  gap={60}
                  wrap={'wrap'}
               >
                  <Stack
                     gap={25}
                     style={{flex: '1 1 340px'}}
                  >
                     <Stack gap={8}>
                        <Badge
                           variant={'light'}
                           color={'blue'}
                           size={'lg'}
                           fw={700}
                           tt={'uppercase'}
                        >
                           A More Complete Clean
                        </Badge>
                        <Text
                           c={siteSectionHeadingHex}
                           fz={
                              isMobile
                                 ? siteTypographySectionTitlePrimaryFontSizeMobile
                                 : siteTypographySectionTitlePrimaryFontSizeDesktop
                           }
                           fw={800}
                           lh={1.1}
                        >
                           Beyond the Standard Wash.
                        </Text>
                     </Stack>
                     <Text
                        c={siteSectionBodyHex}
                        fz={
                           isMobile
                              ? siteTypographySectionLeadFontSizeMobile
                              : siteTypographySectionLeadFontSizeDesktop
                        }
                        fw={500}
                        lh={1.7}
                     >
                        Unlike a regular wash, detailing goes further
                        into the areas that matter most. Clean Park
                        Detailing is ideal for customers who want a
                        deeper clean, improved presentation, and a
                        vehicle that feels properly refreshed.
                     </Text>
                  </Stack>
                  <Card
                     p={50}
                     radius={'xl'}
                     style={{
                        flex: '1 1 340px',
                        background: siteFeatureCardBackgroundHex,
                        boxShadow: siteFeatureCardShadow
                     }}
                  >
                     <Stack gap={22}>
                        <Text
                           c={'white'}
                           fz={
                              isMobile
                                 ? siteTypographySectionLeadFontSizeMobile
                                 : siteTypographySectionLeadFontSizeDesktop
                           }
                           fw={700}
                        >
                           Ideal For
                        </Text>
                        <Stack gap={14}>
                           {whoItIsFor.map((item) => (
                              <Group
                                 key={item}
                                 align={'flex-start'}
                                 gap={14}
                                 wrap={'nowrap'}
                              >
                                 <IconCheck
                                    size={18}
                                    color={'rgba(255,255,255,0.7)'}
                                    style={{marginTop: 3}}
                                 />
                                 <Text
                                    c={'white'}
                                    fz={
                                       isMobile
                                          ? siteTypographySectionLeadFontSizeMobile
                                          : siteTypographySectionLeadFontSizeDesktop
                                    }
                                    fw={500}
                                    lh={1.5}
                                 >
                                    {item}
                                 </Text>
                              </Group>
                           ))}
                        </Stack>
                     </Stack>
                  </Card>
               </Group>
            </Container>
         </Box>

         <Container
            py={isMobile ? 70 : 100}
            size={'xl'}
         >
            <SimpleGrid
               cols={{base: 1, md: 2}}
               spacing={25}
            >
               <Card
                  p={50}
                  radius={siteSectionCardRadiusPx}
                  style={{
                     background: siteFeatureCardBackgroundHex,
                     boxShadow: siteFeatureCardShadow
                  }}
               >
                  <Stack gap={25}>
                     <ThemeIcon
                        p={12}
                        size={60}
                        color={'white'}
                        variant={'light'}
                        radius={'lg'}
                     >
                        <IconBrush
                           stroke={1.6}
                           size={32}
                        />
                     </ThemeIcon>
                     <Stack gap={10}>
                        <Text
                           c={'white'}
                           fz={
                              isMobile
                                 ? siteTypographySectionTitleSecondaryFontSizeMobile
                                 : siteTypographySectionTitleSecondaryFontSizeDesktop
                           }
                           fw={700}
                        >
                           Interior Detailing
                        </Text>
                        <Text
                           c={'rgba(255,255,255,0.75)'}
                           fz={
                              isMobile
                                 ? siteTypographySectionLeadFontSizeMobile
                                 : siteTypographySectionLeadFontSizeDesktop
                           }
                           fw={500}
                           lh={1.65}
                        >
                           Our interior detailing service is designed
                           to leave your vehicle cleaner, fresher, and
                           more comfortable to drive. We focus on the
                           details often missed in a quick clean.
                        </Text>
                     </Stack>
                     <Stack gap={12}>
                        {interiorItems.map((item) => (
                           <Group
                              key={item}
                              align={'flex-start'}
                              gap={12}
                              wrap={'nowrap'}
                           >
                              <IconCheck
                                 size={17}
                                 color={'rgba(255,255,255,0.7)'}
                                 style={{marginTop: 2}}
                              />
                              <Text
                                 c={'white'}
                                 fz={
                                    isMobile
                                       ? siteTypographySectionLeadFontSizeMobile
                                       : siteTypographySectionLeadFontSizeDesktop
                                 }
                                 fw={500}
                                 lh={1.5}
                              >
                                 {item}
                              </Text>
                           </Group>
                        ))}
                     </Stack>
                  </Stack>
               </Card>
               <Card
                  p={50}
                  radius={'xl'}
                  bg={'white'}
                  style={{
                     boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                     border: '1px solid #e8ecf4'
                  }}
               >
                  <Stack gap={25}>
                     <ThemeIcon
                        p={12}
                        size={60}
                        color={siteSectionHeadingHex}
                        variant={'light'}
                        radius={'lg'}
                     >
                        <IconSparkles
                           stroke={1.6}
                           size={32}
                        />
                     </ThemeIcon>
                     <Stack gap={10}>
                        <Text
                           c={siteSectionHeadingHex}
                           fz={
                              isMobile
                                 ? siteTypographySectionTitleSecondaryFontSizeMobile
                                 : siteTypographySectionTitleSecondaryFontSizeDesktop
                           }
                           fw={700}
                        >
                           Exterior Detailing
                        </Text>
                        <Text
                           c={siteSectionBodyHex}
                           fz={
                              isMobile
                                 ? siteTypographySectionLeadFontSizeMobile
                                 : siteTypographySectionLeadFontSizeDesktop
                           }
                           fw={500}
                           lh={1.65}
                        >
                           Our exterior detailing service is all about
                           improving the overall look of your vehicle
                           and helping maintain its finish. We take
                           the time to work through the areas that
                           need extra attention.
                        </Text>
                     </Stack>
                     <Stack gap={12}>
                        {exteriorItems.map((item) => (
                           <Group
                              key={item}
                              align={'flex-start'}
                              gap={12}
                              wrap={'nowrap'}
                           >
                              <IconCheck
                                 size={17}
                                 color={siteSectionHeadingHex}
                                 style={{marginTop: 2}}
                              />
                              <Text
                                 c={siteSectionBodyHex}
                                 fz={
                                    isMobile
                                       ? siteTypographySectionLeadFontSizeMobile
                                       : siteTypographySectionLeadFontSizeDesktop
                                 }
                                 fw={500}
                                 lh={1.5}
                              >
                                 {item}
                              </Text>
                           </Group>
                        ))}
                     </Stack>
                  </Stack>
               </Card>
            </SimpleGrid>
         </Container>

         <Box bg={siteFeatureCardBackgroundHex}>
            <Container
               py={isMobile ? 70 : 100}
               size={'xl'}
            >
               <Stack
                  align={'center'}
                  gap={20}
               >
                  <Stack
                     gap={12}
                     align={'center'}
                  >
                     <Text
                        c={siteFeatureCardMutedLabelHex}
                        fw={700}
                        fz={
                           isMobile
                              ? siteTypographyEyebrowFontSizeMobile
                              : siteTypographyEyebrowFontSizeDesktop
                        }
                        tt={'uppercase'}
                        lts={2}
                        ta={'center'}
                     >
                        Why Choose Clean Park Detailing
                     </Text>
                     <Text
                        c={'white'}
                        fz={
                           isMobile
                              ? siteTypographySectionTitlePrimaryFontSizeMobile
                              : siteTypographySectionTitlePrimaryFontSizeDesktop
                        }
                        fw={800}
                        lh={1.1}
                        ta={'center'}
                     >
                        Convenience, Care, and Quality.
                     </Text>
                  </Stack>
                  <Stack
                     gap={14}
                     maw={660}
                     align={'center'}
                  >
                     <Text
                        c={'rgba(255,255,255,0.8)'}
                        fz={
                           isMobile
                              ? siteTypographySectionLeadFontSizeMobile
                              : siteTypographySectionLeadFontSizeDesktop
                        }
                        fw={500}
                        lh={1.7}
                        ta={'center'}
                     >
                        As part of the Clean Park brand, Clean Park
                        Detailing gives local customers in
                        Morrinsville access to both everyday wash
                        options and more premium vehicle care in one
                        trusted place.
                     </Text>
                     <Text
                        c={'rgba(255,255,255,0.8)'}
                        fz={
                           isMobile
                              ? siteTypographySectionLeadFontSizeMobile
                              : siteTypographySectionLeadFontSizeDesktop
                        }
                        fw={500}
                        lh={1.7}
                        ta={'center'}
                     >
                        We are local, approachable, and committed to
                        delivering results that make a real difference
                        to the look and feel of your vehicle. When a
                        basic wash is not enough, Clean Park Detailing
                        gives your vehicle the extra attention it
                        deserves.
                     </Text>
                  </Stack>
               </Stack>
            </Container>
         </Box>
      </Stack>
   )
}
